import React, { ComponentPropsWithoutRef, useState } from 'react';
import { MBText } from '@shared/components/MBText';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { MBLoadingIndicator } from '@shared/components/loading';
import { MBAutosizeTextInput } from '@shared/components/textInput';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { useValidateUploads } from '@shared/scenes/upload/hooks';
import { Colors, grid } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { MBAttachmentButton } from '../containers/attachmentButton';
import { MBAttachmentList } from '../containers/attachmentList';
import { MBChatInputPlusButton } from '../containers/plusButton';
import { MBChatInputSendButton } from '../containers/sendButton';
import { MBEmojiButton } from './emojiButton';
import { Styles } from './styles';

export interface ChatInputBarProps {
  draftId: string;
  value: string;
  isEditing: boolean;
  hasAttachments: boolean;
  placeholder?: string;
  hideUrgent?: boolean;
  autoFocus?: boolean;
  innerRef?: React.RefObject<MBAutosizeTextInput>;
  onBlur?(): void;
  onFocus?(): void;
  onChangeText(text: string): void;
  onCursorPositionChange?(position: number): void;
  onPasteFiles(files: File[]): void;
  onSend(): void;
  onSubmitEditing(): void;
  onCancelEdit(): void;
  onAddEmoji(emoji: string): void;
}

export function ChatInputBar({
  draftId,
  value,
  isEditing,
  hasAttachments,
  placeholder,
  hideUrgent,
  autoFocus,
  innerRef,
  onBlur,
  onFocus,
  onChangeText,
  onCursorPositionChange,
  onPasteFiles,
  onSend,
  onSubmitEditing,
  onCancelEdit,
  onAddEmoji,
}: ChatInputBarProps) {
  const [sending, setSending] = useState(false);
  const canSend = !!value.length || hasAttachments;

  const send = async () => {
    if (!canSend) {
      return;
    }
    setSending(true);
    try {
      onSend();
    } finally {
      setSending(false);
    }
  };

  const change = (text: string) => {
    if (!sending) {
      onChangeText(text);
    }
  };

  return (
    <MBView paddingVertical={0.625} style={Styles.container}>
      <View row shrinkWrap fullWidth style={{ minHeight: 42, alignItems: 'flex-end' }}>
        <View style={{ justifyContent: 'flex-end', flexGrow: 0, flexShrink: 0 }}>
          {!isEditing && <MBPlusButtonWrapped draftId={draftId} hideUrgent={hideUrgent} />}
          {isEditing && <View paddingLeft={1} />}
        </View>

        <MBView flex style={Styles.inputContainer}>
          <MBAttachmentList draftId={draftId} />
          <View row relative>
            <MBAutosizeTextInput
              style={{
                flexGrow: 1,
                paddingVertical: grid(1),
                paddingLeft: 0,
                paddingRight: grid(5),
              }}
              ref={innerRef}
              placeholder={placeholder}
              size="normal"
              autoFocus={autoFocus}
              padding="normal"
              value={value}
              onChangeText={change}
              maxRows={10}
              onPasteFiles={onPasteFiles}
              onSubmitEditing={onSubmitEditing}
              onCursorPositionChange={onCursorPositionChange}
              onBlur={onBlur}
              onFocus={onFocus}
            />
          </View>
          <View style={{ position: 'absolute', right: 0, bottom: 1 }}>
            <MBEmojiButton onSelect={onAddEmoji} />
          </View>
        </MBView>

        <View style={{ justifyContent: 'flex-end', flexGrow: 0, flexShrink: 0 }}>
          {!!sending && (
            <MBLoadingIndicator
              style={[Styles.iconButton, Styles.alignBottom]}
              color={Colors.v2.purple}
            />
          )}
          {!sending && canSend && (
            <MBChatInputSendButton
              draftId={draftId}
              style={Styles.alignBottom}
              onPress={send}
              testID="chat-send-button"
            />
          )}
          {!isEditing && !sending && !canSend && <MBAttachmentButtonWrapped draftId={draftId} />}
        </View>
      </View>

      {isEditing && (
        <MBView row wrap style={Styles.cancelButton}>
          <MBTouchableOpacity onPress={onCancelEdit} style={{ paddingHorizontal: 5 }}>
            <MBText>{i18n('common.cancel')}</MBText>
          </MBTouchableOpacity>
        </MBView>
      )}
    </MBView>
  );
}

function MBAttachmentButtonWrapped({
  draftId,
}: Omit<ComponentPropsWithoutRef<typeof MBAttachmentButton>, 'validateUploads'>) {
  const validateUploads = useValidateUploads({ showUpgradeModal: true });

  return <MBAttachmentButton draftId={draftId} validateUploads={validateUploads} />;
}

function MBPlusButtonWrapped({
  draftId,
  hideUrgent,
}: Omit<ComponentPropsWithoutRef<typeof MBChatInputPlusButton>, 'validateUploads'>) {
  const validateUploads = useValidateUploads({ showUpgradeModal: true });

  return (
    <MBChatInputPlusButton
      draftId={draftId}
      hideUrgent={hideUrgent}
      validateUploads={validateUploads}
    />
  );
}
